import { PartThumbnail as SharedPartThumbnail } from '../../components/PartThumbnail'
import { cx } from '../../components/ui/cx'

export interface RfqPartThumbnailProps {
  /** `part.thumbnailRef` (or the workspace-tab equivalent) — the real,
   * resolved thumbnail URL, or null/undefined on a miss. */
  readonly url: string | null | undefined
  /** The call site's own box-sizing class (e.g. `rfq-part-thumb`,
   * `new-rfq-modal-row-thumb`, `workspace-rfq-thumb`) — applied to BOTH the
   * real `<img>` and the placeholder, so a resolved image never shifts the
   * row layout (mirrors `CatalogResultThumb`'s own `className` contract). */
  readonly className?: string
}

/**
 * UID-517 item 5 — the RFQ/workspace real-thumbnail-or-placeholder box.
 *
 * Reuses the SHARED `PartThumbnail` real-image/onError-fallback machinery
 * (`components/PartThumbnail.tsx`) with the SAME decorative glyph
 * placeholder cart lines already fall back to
 * (`verticals/cart/PartThumbnail.tsx`), via the shared
 * `.part-thumbnail-placeholder-glyph` class (`styles/ui.css`) — NOT
 * `CatalogResultThumb`'s informative "No image available" caption treatment.
 *
 * That treatment is `/catalog` search's own and stays untouched
 * (`CatalogResultThumb` is unmodified and still used verbatim by
 * ResultCard/SeriesCard/ConfiguratorCore/CategoryTileImage/RecentlyViewed/
 * CatalogBrowsePage). This component is the RFQ-side sibling for
 * RfqListRow/RfqPartsCard/NewRfqModal and the workspace RFQ tab, which used
 * to render `CatalogResultThumb` for a surface that is NOT catalog search —
 * a real photo there is decorative context on an RFQ row, not the content
 * itself, so a decorative glyph (not an informative caption) is the correct
 * placeholder, matching the cart/order convention this whole ticket (UID-517)
 * extends to catalog-kind parts.
 *
 * W5 — keyed on `url` itself: `SharedPartThumbnail`'s own doc comment
 * requires this ("callers should key this component on the url itself when
 * it can change across a refetch"), since its load-failure flag is local and
 * permanent for the instance. Without the key, a line whose url changes
 * across a refetch (e.g. the catalog resolves a real photo after an initial
 * miss, or the SPA polls) would keep rendering a stale 404's placeholder
 * forever instead of retrying the new url — exactly what `CatalogResultThumb`
 * avoided with its own internal `lastImageRef` reset.
 */
export function PartThumbnail({ url, className }: RfqPartThumbnailProps) {
  return (
    <SharedPartThumbnail
      key={url ?? 'placeholder'}
      url={url}
      imgClassName={cx(className)}
      placeholder={<RfqThumbnailPlaceholder className={className} />}
    />
  )
}

function RfqThumbnailPlaceholder({ className }: { readonly className?: string | undefined }) {
  return (
    <span className={cx(className, 'part-thumbnail-placeholder-glyph')} aria-hidden="true">
      {/* Isometric 3D box glyph (Lucide "box") — same glyph the cart-line
          placeholder uses, so a part's decorative "no image" tile reads
          identically across cart/order/RFQ. */}
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
        <path d="m3.3 7 8.7 5 8.7-5" />
        <path d="M12 22V12" />
      </svg>
    </span>
  )
}
